import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  StyleProp,
  ViewStyle,
  Platform,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { type AttachmentActionsProps } from '../../types/attachmentInterfaces';
import { Download, Trash2 } from '../icons';
import { networkService } from '../../services/network';
import { showToast } from '../../utils/toast';
import { showConfirmation } from '../../utils/confirmation';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { ATTACHMENT_COLORS } from '../../constants/attachmentColors';

// M-3: Module-scope constant — not recreated on every render
const MAX_DOWNLOAD_RETRIES = 3;

// Icon sizes per layout context
const ICON_SIZES: Record<AttachmentActionsProps['layout'], number> = {
  vertical: 18,    // compact mobile card
  'icons-only': 20, // desktop table
};

/**
 * AttachmentActions component
 * Displays action buttons for attachment (Download, Delete).
 * Supports offline guard for both operations.
 */
export const AttachmentActions: React.FC<AttachmentActionsProps> = ({
  attachment,
  isEditing,
  isDeleting,
  onDownload,
  onDelete,
  layout,
  disabled = false,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDownload = async (retryCount: number = 0) => {
    const isOnline = networkService.isConnected();
    if (!isOnline) {
      if (Platform.OS === 'web') {
        alert('Download is unavailable while offline');
      } else {
        Alert.alert('Offline', 'Download is unavailable while offline');
      }
      return;
    }

    setIsDownloading(true);
    try {
      await onDownload();
      if (Platform.OS === 'android') {
        showToast.success('File saved to Downloads');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download attachment';

      if (retryCount < MAX_DOWNLOAD_RETRIES) {
        const shouldRetry = await showConfirmation(
          'Download Failed',
          errorMessage,
          'Retry',
          'Cancel',
        );

        if (shouldRetry) {
          setIsDownloading(false);
          await handleDownload(retryCount + 1);
          return;
        }
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    const isOnline = networkService.isConnected();
    if (!isOnline) {
      showToast.error('Delete is unavailable while offline');
      return;
    }

    onDelete();
  };

  const iconSize = ICON_SIZES[layout];
  const isDisabled = disabled || isDeleting;

  // M-1: Shared icon button pair — parameterised by container style and optional testID
  const renderIconButtons = (containerStyle: StyleProp<ViewStyle>, testID?: string) => (
    <View style={containerStyle} testID={testID}>
      <TouchableOpacity
        onPress={handleDownload}
        disabled={isDisabled || isDownloading}
        style={[styles.iconButton, (isDisabled || isDownloading) && styles.disabledButton]}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={`Download ${attachment.attachmentName}`}
        testID="attachment-download-btn"
      >
        {isDownloading ? (
          <ActivityIndicator size="small" color={ATTACHMENT_COLORS.ACTION_BUTTON_COLOR} />
        ) : (
          <Download size={iconSize} color={ATTACHMENT_COLORS.ACTION_BUTTON_COLOR} />
        )}
      </TouchableOpacity>
      {layout === 'vertical' ? (
        <View
          style={isEditing ? undefined : styles.hiddenButton}
          pointerEvents={isEditing ? 'auto' : 'none'}
        >
          <TouchableOpacity
            onPress={handleDelete}
            disabled={isDisabled}
            style={[styles.iconButton, isDisabled && styles.disabledButton]}
            accessible={isEditing}
            accessibilityRole="button"
            accessibilityLabel={isEditing ? `Delete ${attachment.attachmentName}` : undefined}
            testID="attachment-delete-btn"
          >
            <Trash2 size={iconSize} color={ATTACHMENT_COLORS.DELETE_BUTTON_COLOR} />
          </TouchableOpacity>
        </View>
      ) : isEditing ? (
        <TouchableOpacity
          onPress={handleDelete}
          disabled={isDisabled}
          style={[styles.iconButton, isDisabled && styles.disabledButton]}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={`Delete ${attachment.attachmentName}`}
          testID="attachment-delete-btn"
        >
          <Trash2 size={iconSize} color={ATTACHMENT_COLORS.DELETE_BUTTON_COLOR} />
        </TouchableOpacity>
      ) : null}
    </View>
  );

  const deleteConfirmDialog = (
    <ConfirmationDialog
      visible={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDeleteConfirm}
      title="Delete Attachment"
      message={`This action cannot be undone.\n\nItem: ${attachment.attachmentName}`}
      confirmText="Delete"
      cancelText="Cancel"
      confirmButtonStyle="danger"
    />
  );

  if (layout === 'vertical') {
    // Native mobile card: icons stacked in a column, spread to top/bottom of card height
    return (
      <>
        {renderIconButtons(styles.verticalContainer, __DEV__ ? 'attachment-actions-vertical' : undefined)}
        {deleteConfirmDialog}
      </>
    );
  }

  // Desktop table: icons side by side in a row
  return (
    <>
      {renderIconButtons(styles.iconsOnlyContainer)}
      {deleteConfirmDialog}
    </>
  );
};

const styles = StyleSheet.create({
  // L-1: Shared base properties; only flexDirection and justification differ between containers
  // minHeight: 56 ensures two icon tap targets (each ~26px) always fit even in shortest cards
  verticalContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 56,
  },
  iconsOnlyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconButton: {
    padding: 4,
  },
  disabledButton: {
    opacity: 0.4,
  },
  hiddenButton: {
    opacity: 0,
  },
});
